import { API_BASE_URL } from '../constants';
import { CartItem } from '../context/CartContext';
import { User } from './authService';
import { Order as CustomerOrder, OrderApiResponse, OrderFeedback, OrderFeedbackResponse, SingleOrderApiResponse } from '../types/BookingTypes';

export enum OrderStatus {
  CREATED = 'CREATED',
  CONFIRMED = 'CONFIRMED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

export enum PaymentStatus {
  PENDING = 'PENDING',
  PAID = 'PAID',
  FAILED = 'FAILED',
  REFUNDED = 'REFUNDED'
}

export interface OrderItem {
  id: number;
  orderId: number;
  serviceId: number;
  serviceName: string;
  serviceType: string;
  actualPrice: number;
  serviceCost: number;
  quantity: number;
  itemTotal: number;
  serviceStatus: string;
  notes: string;
  warrantyPeriod: number;
  createdAt: string;
  updatedAt: string;
  createdBy: number;
  warrantyTypeId: number;
  warrantyName: string;
  warrantyTypeDescription: string;
  warrantyCoveredItems: string;
  warrantyNotCoveredItems: string;
  warrantyEndDate: string;
}

export interface OrderStatusHistory {
  id: number;
  orderId: number;
  status: string;
  notes: string;
  createdAt: string;
  createdBy: number;
  order: string;
}

export interface OrderPayment {
  id: number;
  orderId: number;
  paymentMethod: string;
  paymentAmount: number;
  transactionId: string;
  paymentStatus: PaymentStatus;
  notes: string;
  createdAt: string;
  createdBy: number;
  updatedAt: string;
  updatedBy: number;
  isActive: boolean;
  order: string;
}

export interface Order {
  id: number;
  orderNumber: string;
  customerMobile: string;
  customerName: string;
  customerEmail: string;
  customerAddress: string;
  makeId: number;
  makeName: string;
  modelId: number;
  modelName: string;
  orderStatus: OrderStatus;
  orderDate: string;
  expectedDeliveryDate: string;
  actualDeliveryDate: string;
  lastUpdated: string;
  totalItemPrice: number;
  totalServiceCost: number;
  discountAmount: number;
  gstPercentage: number;
  gstAmount: number;
  finalAmount: number;
  paymentStatus: PaymentStatus;
  paymentMethod: string;
  amountPaid: number;
  balanceAmount: number;
  technicianId: number;
  technicianName: string;
  notes: string;
  deviceCondition: string;
  warrantyPeriod: number;
  warrantyEndDate: string;
  createdAt: string;
  createdBy: number;
  updatedAt: string;
  updatedBy: number;
  isActive: boolean;
  orderItems: OrderItem[];
  orderStatusHistory: OrderStatusHistory[];
  orderPayments: OrderPayment[];
}

export interface ModelReview {
  id: number;
  customerName: string;
  rating: number;
  comment: string;
  photoUrl: string;
  createdAt: string;
  modelId: number;
}

interface ModelReviewResponse {
  entity: ModelReview[];
  isSuccess: boolean;
  errors: any;
  exception: any;
  message: string | null;
}

export const createOrder = async (
  cartItems: CartItem[],
  user: User,
  address: { label: string; address: string; landmark?: string },
  paymentMethod: string,
  bill: {
    itemsTotal: number;
    serviceTotal: number;
    gstOnService: number;
    doorstepCharges: number;
    onlinePaymentDiscount: number;
    total: number;
  }
): Promise<Order> => {
  try {
    // Get the first cart item for make/model details
    const firstItem = cartItems[0];

    const orderItems = cartItems.flatMap(item =>
      item.services.map(service => ({
        serviceId: service.id,
        serviceName: service.serviceName,
        serviceType: service.serviceName,
        actualPrice: service.actualPrice,
        serviceCost: service.serviceCost,
        quantity: 1,
        itemTotal: service.actualPrice + service.serviceCost,
        warrantyPeriod: service.warrantyPeriod,
        warrantyTypeId: service.warrantyTypeId,
        warrantyName: service.warrantyName,
        warrantyTypeDescription: service.warrantyTypeDescription,
        warrantyCoveredItems: service.warrantyCoveredItems,
        warrantyNotCoveredItems: service.warrantyNotCoveredItems
      }))
    );

    // Calculate max warrantyPeriod (in days) from all services
    const maxWarrantyPeriod = Math.max(
      ...cartItems.flatMap(item => item.services.map(service => service.warrantyPeriod || 0))
    );
    // Calculate warrantyEndDate as Today + maxWarrantyPeriod
    const today = new Date();
    const warrantyEndDate = new Date(today.getTime() + maxWarrantyPeriod * 24 * 60 * 60 * 1000)
      .toISOString().split('T')[0]; // Format as YYYY-MM-DD

    const orderData = {
      customerMobile: user.phoneNumber,
      customerName: user.name || '',
      customerEmail: '',
      customerAddress: `${address.address}${address.landmark ? `, Landmark: ${address.landmark}` : ''}`,
      makeId: firstItem.model.brandId,
      makeName: firstItem.model.name.split(' ')[0], // Assuming first word is brand name
      modelId: firstItem.model.id,
      modelName: firstItem.model.name,
      orderStatus: OrderStatus.CREATED,
      paymentStatus: PaymentStatus.PENDING,
      totalItemPrice: bill.itemsTotal,
      totalServiceCost: bill.serviceTotal,
      discountAmount: bill.onlinePaymentDiscount,
      gstPercentage: 18,
      gstAmount: bill.gstOnService,
      finalAmount: bill.total,
      paymentMethod: paymentMethod,
      orderItems: orderItems,
      warrantyEndDate // <-- add this field
    };

    const response = await fetch(`${API_BASE_URL}/order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(orderData),
    });

    if (!response.ok) {
      throw new Error('Failed to create order');
    }

    const data = await response.json();
    
    if (!data.isSuccess || !data.entity) {
      throw new Error(data.errors || data.message || 'Failed to create order');
    }

    return data.entity;
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

export const getCustomerOrders = async (phoneNumber: string): Promise<CustomerOrder[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/Order/customer/${phoneNumber}`, {
      method: 'GET',
      headers: {
        'accept': '*/*',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch orders');
    }

    const data: OrderApiResponse = await response.json();

    if (!data.isSuccess) {
      throw new Error(data.errors || 'Failed to fetch orders');
    }

    return data.entity;
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }
};

export const submitOrderFeedback = async (
  orderId: number,
  rating: number,
  comment: string,
  photo: File | null
): Promise<OrderFeedback> => {
  try {
    const formData = new FormData();
    formData.append('orderId', orderId.toString());
    formData.append('rating', rating.toString());
    formData.append('comment', comment);
    if (photo) {
      formData.append('feedbackImage', photo);
    }

    const response = await fetch(`${API_BASE_URL}/orderfeedback/with-photo`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to submit feedback');
    }

    const data: OrderFeedbackResponse = await response.json();

    if (!data.isSuccess) {
      throw new Error(data.errors || 'Failed to submit feedback');
    }

    return data.entity;
  } catch (error) {
    console.error('Error submitting feedback:', error);
    throw error;
  }
};

export const getOrderFeedback = async (orderId: number): Promise<OrderFeedback | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/orderfeedback/${orderId}`, {
      method: 'GET',
      headers: {
        'accept': '*/*',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error('Failed to fetch feedback');
    }

    const data: OrderFeedbackResponse = await response.json();

    if (!data.isSuccess) {
      throw new Error(data.errors || 'Failed to fetch feedback');
    }

    return data.entity;
  } catch (error) {
    console.error('Error fetching feedback:', error);
    throw error;
  }
};

export const getModelReviews = async (modelId: number): Promise<ModelReview[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/OrderFeedback/model/${modelId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch model reviews');
    }
    const data: ModelReviewResponse = await response.json();
    return data.isSuccess ? data.entity : [];
  } catch (error) {
    console.error('Error fetching model reviews:', error);
    return [];
  }
};

export const getOrderById = async (orderId: string): Promise<Order | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/Order/${orderId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch order details');
    }

    const data: SingleOrderApiResponse = await response.json();

    if (!data.isSuccess) {
      throw new Error(data.errors || 'Failed to fetch feedback');
    }

    // Map the API response to our Order type, handling nullable fields
    if (!data.entity) return null;

    const entity = data.entity;
    const currentDate = new Date().toISOString();
    
    return {
      ...entity,
      orderStatus: entity.orderStatus as OrderStatus,
      paymentStatus: entity.paymentStatus as PaymentStatus,
      expectedDeliveryDate: entity.expectedDeliveryDate || '',
      actualDeliveryDate: entity.actualDeliveryDate || '',
      lastUpdated: entity.lastUpdated || currentDate,
      technicianName: entity.technicianName || '',
      notes: entity.notes || '',
      deviceCondition: entity.deviceCondition || '',
      warrantyEndDate: entity.warrantyEndDate || '',
      technicianId: entity.technicianId || 0,
      warrantyPeriod: entity.warrantyPeriod || 0,
      createdBy: entity.createdBy || 0,
      updatedBy: entity.updatedBy || 0,
      createdAt: entity.createdAt || currentDate,
      updatedAt: entity.updatedAt || currentDate,
      orderDate: entity.orderDate || currentDate
    };
  } catch (error) {
    console.error('Error fetching order:', error);
    throw error;
  }
}; 